import { Stack, Typography } from '@mui/material';
import { ComponentType } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { IconProps } from '@/components/icons/ThumbsUpIcon';
import { Card } from '@/components/layout/Card/Card';
import { BRAND, COLORS } from '@/styles/tokens/colors';

export interface ProminentActionBannerAction {
  label: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
}

export interface ProminentActionBannerProps {
  /** Icon rendered inside the accent-colored badge on the left. */
  icon: ComponentType<IconProps>;
  header: React.ReactNode;
  description?: React.ReactNode;
  /** Optional call-to-action button rendered on the right. */
  action?: ProminentActionBannerAction;
}

/**
 * A presentational banner that pairs an accent-colored icon badge with a
 * header, description, and an optional call-to-action button. The accent
 * uses the Luminary AI orange (matching the "Ask Luminary AI" entry point)
 * to visually distinguish it from primary actions.
 *
 * This is purely presentational — callers own visibility, data, and the
 * action handler.
 */
export function ProminentActionBanner({
  icon: Icon,
  header,
  description,
  action,
}: ProminentActionBannerProps) {
  return (
    <Card
      variant="outlined"
      // Banners are typically rendered inside column flex layouts; without
      // this they get squeezed and clip their content at short viewport
      // heights.
      sx={{ p: 1.5, borderRadius: '4px', flexShrink: 0 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        width="100%"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ bgcolor: BRAND.ORANGE, borderRadius: '8px', p: 0.5 }}
          >
            {/* Set both color and fill so the badge reads solid white
                whether the icon renders via currentColor or a fill. */}
            <Icon
              size={16}
              color={COLORS.PRIMITIVES.WHITE}
              fill={COLORS.PRIMITIVES.WHITE}
            />
          </Stack>
          <Stack gap={0.25}>
            <Typography variant="title-xs">{header}</Typography>
            {description && (
              // The design uses the 12px/medium "text-secondary" style but in
              // the primary text color (not the default secondary gray), so we
              // override the color here to match.
              <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
                {description}
              </Typography>
            )}
          </Stack>
        </Stack>
        {action && (
          <Button
            variant="prominent"
            size="xs"
            loading={action.loading}
            onClick={action.onClick}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {action.label}
          </Button>
        )}
      </Stack>
    </Card>
  );
}
